import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TeamTreeNode } from './TeamTree'

interface MoveTeamDialogProps {
  team: TeamTreeNode
  allTeams: TeamTreeNode[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

function flattenTree(nodes: TeamTreeNode[]): TeamTreeNode[] {
  const result: TeamTreeNode[] = []
  for (const node of nodes) {
    result.push(node)
    if (node.children.length > 0) {
      result.push(...flattenTree(node.children))
    }
  }
  return result
}

function getDescendantIds(node: TeamTreeNode): Set<number> {
  const ids = new Set<number>()
  for (const child of node.children) {
    ids.add(child.id)
    for (const id of getDescendantIds(child)) {
      ids.add(id)
    }
  }
  return ids
}

function getSubtreeMaxDepth(node: TeamTreeNode): number {
  if (node.children.length === 0) return node.depth
  return Math.max(...node.children.map(getSubtreeMaxDepth))
}

const MAX_DEPTH = 5

export function MoveTeamDialog({ team, allTeams, open, onOpenChange }: MoveTeamDialogProps) {
  const [parentId, setParentId] = useState<string>('')
  const [processing, setProcessing] = useState(false)

  const descendantIds = getDescendantIds(team)
  const subtreeHeight = getSubtreeMaxDepth(team) - team.depth

  const flatTeams = flattenTree(allTeams)
  const validParents = flatTeams.filter((t) => {
    if (t.id === team.id) return false
    if (descendantIds.has(t.id)) return false
    // Check depth: moving team under target means target.depth + 1 + subtreeHeight must be < MAX_DEPTH
    if (t.depth + 1 + subtreeHeight >= MAX_DEPTH) return false
    return true
  })

  const handleMove = () => {
    setProcessing(true)
    router.patch(
      `/admin/teams/${team.id}/move`,
      { parent_id: parentId === '__root__' ? null : parentId },
      {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
        onFinish: () => setProcessing(false),
      }
    )
  }

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setParentId('')
      setProcessing(false)
    }
    onOpenChange(value)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Move &ldquo;{team.name}&rdquo;</AlertDialogTitle>
          <AlertDialogDescription>
            Select a new parent team. The team and all its children will be moved.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-2">
          <Select value={parentId} onValueChange={setParentId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select parent team..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__root__">None (make root team)</SelectItem>
              {validParents.map((t) => (
                <SelectItem key={t.id} value={t.id.toString()}>
                  {'  '.repeat(t.depth)}{t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleMove}
            disabled={!parentId || processing}
          >
            {processing ? 'Moving...' : 'Move'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
